import { getMedicationSourceSuggestions } from './medicationSourceSuggestions';
import { getCareSourceKnowledgeSuggestions, hasCareSourceSavedTimelineRecords } from './careSourceStatusModel';
import { getDirectProfileReviewItems, getProfileChapterLabel } from './DirectProfileOrganizationReview';
import { getKnowledgeItemProfileChapterId } from '../knowledgeItems/careKnowledgeProfileGrouping';
import { getPersonIdentityProfileSuggestions } from './aiReviewPolicy';
import { getCareKnowledgePromotionDecision, isAutomaticallyPersistableCareKnowledge } from './careKnowledgeDurabilityPolicy';
import {
  CAPTURE_DESTINATIONS,
  getCaptureSuggestionDestination,
} from './captureDestinationPolicy';

const normalize = (value = '') => String(value || '').trim().toLowerCase();
const ALREADY_KNOWN_STATUSES = new Set(['already_known', 'duplicate', 'unchanged']);
const UNCLASSIFIED_STATUSES = new Set(['unclassified', 'unable_to_classify']);

const addDestination = (destinations, label) => {
  destinations[label] = (destinations[label] || 0) + 1;
};

const getDestinationLabels = (destinations = {}) => (
  Object.entries(destinations)
    .filter(([, count]) => Number(count) > 0)
    .map(([label]) => label)
);

const REVIEW_DESTINATION_BY_CATEGORY = Object.freeze({
  medication: 'Medications',
  allergy: 'Allergies',
  goal: 'Goals',
  appointment: 'Appointments',
});

const getReviewDestination = (item = {}, promotionDecision = {}) => {
  const routedDestination = getCaptureSuggestionDestination(item);
  if (REVIEW_DESTINATION_BY_CATEGORY[promotionDecision.category]) {
    return REVIEW_DESTINATION_BY_CATEGORY[promotionDecision.category];
  }
  if (routedDestination === CAPTURE_DESTINATIONS.CONTACTS) return 'Contacts';
  if (routedDestination === CAPTURE_DESTINATIONS.HEALTH_PROFILE) return 'Health Profile';
  if (routedDestination === CAPTURE_DESTINATIONS.DOCUMENT) return 'Document';
  if (routedDestination === CAPTURE_DESTINATIONS.TIMELINE) return 'Timeline';
  return routedDestination || 'Profile';
};

export const getTouchedCategoryLabels = (source = {}) => {
  const summary = buildExtractionResultSummary(source);
  const savedLabels = getDestinationLabels(summary.savedDestinations);
  const automaticLabels = getDestinationLabels(summary.automaticSavedDestinations);
  const timelineLabels = hasCareSourceSavedTimelineRecords(source) ? ['Timeline'] : [];
  if (savedLabels.length || automaticLabels.length || timelineLabels.length) {
    return [...new Set([...savedLabels, ...automaticLabels, ...timelineLabels])];
  }

  if (summary.reviewTotal > 0) {
    return getDestinationLabels(summary.destinations);
  }

  return [];
};

export const buildExtractionResultSummary = (source = {}) => {
  const suggestions = getCareSourceKnowledgeSuggestions(source);
  const rawSuggestionsById = new Map(
    (Array.isArray(source.suggestedCareKnowledgeItems) ? source.suggestedCareKnowledgeItems : [])
      .map((item, index) => [String(item?.clientId || item?.id || `knowledge-${index + 1}`), item])
  );
  const directItems = getDirectProfileReviewItems(source, suggestions);
  const directIds = new Set(directItems.map((item) => item.clientId).filter(Boolean));
  const decisions = source.directProfileReviewDecisions || {};
  const destinations = {};
  const pendingDestinations = {};
  const savedDestinations = {};
  const automaticItems = [];
  let automaticTotal = 0;
  let reviewTotal = 0;
  let reviewed = 0;
  let skipped = 0;
  let alreadyKnown = 0;
  let unableToClassify = 0;
  let timelineOnly = 0;
  const automaticIds = new Set();

  suggestions.forEach((item) => {
    const rawItem = rawSuggestionsById.get(item.clientId) || item;
    const itemStatus = normalize(
      rawItem.reviewStatus
      || rawItem.classificationStatus
      || rawItem.deduplicationStatus
    );
    if (rawItem.alreadyKnown === true || rawItem.isDuplicate === true || ALREADY_KNOWN_STATUSES.has(itemStatus)) {
      alreadyKnown += 1;
      return;
    }
    if (rawItem.unableToClassify === true || UNCLASSIFIED_STATUSES.has(itemStatus)) {
      unableToClassify += 1;
      return;
    }

    let destination = '';
    const routedDestination = getCaptureSuggestionDestination(item);
    const promotionDecision = getCareKnowledgePromotionDecision(rawItem);
    if (promotionDecision.needsCaregiverDecision) {
      if (directIds.has(item.clientId)) {
        destination = getProfileChapterLabel(item.profileChapterId);
      } else {
        destination = getReviewDestination(item, promotionDecision);
      }
    } else if (directIds.has(item.clientId)) {
      destination = getProfileChapterLabel(item.profileChapterId);
    }

    if (!destination) {
      if (!isAutomaticallyPersistableCareKnowledge(rawItem)) {
        timelineOnly += 1;
        return;
      }
      automaticTotal += 1;
      if (item.clientId) automaticIds.add(item.clientId);
      automaticItems.push({
        clientId: item.clientId,
        destination: getProfileChapterLabel(
          getKnowledgeItemProfileChapterId(rawItem)
        ),
      });
      return;
    }

    reviewTotal += 1;
    addDestination(destinations, destination);
    const decision = decisions[item.clientId];
    if (!decision) addDestination(pendingDestinations, destination);
    if (decision) reviewed += 1;
    if (decision === 'skip') skipped += 1;
    if (decision === 'add') addDestination(savedDestinations, destination);
  });

  const legacyGroups = directItems.length ? [] : getPersonIdentityProfileSuggestions(source);
  legacyGroups.forEach((group) => group.items.forEach((_, index) => {
    const destination = {
      about: 'Person',
      earlyWarningSigns: 'Watch For',
      thingsThatUsuallyHelp: 'What Helps',
      thingsToAvoid: 'Watch For',
      safetyConsiderations: 'Safety',
      communicationStyle: 'Communication',
      communicationNotes: 'Communication',
      familySupportTips: 'What Helps',
      schoolTherapySuggestions: 'Health & Care',
    }[group.key] || 'Profile';
    reviewTotal += 1;
    addDestination(destinations, destination);
    const decision = decisions[`legacy-profile:${group.key}:${index}`];
    if (!decision) addDestination(pendingDestinations, destination);
    if (decision) reviewed += 1;
    if (decision === 'skip') skipped += 1;
    if (decision === 'add') addDestination(savedDestinations, destination);
  }));

  const medicationCount = getMedicationSourceSuggestions(source).length;
  const medicationItemsAlreadyCounted = suggestions.filter(
    (item) => normalize(item.knowledgeType) === 'medication'
  ).length;
  const additionalMedicationCount = Math.max(0, medicationCount - medicationItemsAlreadyCounted);
  reviewTotal += additionalMedicationCount;
  if (additionalMedicationCount) {
    destinations.Medications = (destinations.Medications || 0) + additionalMedicationCount;
    pendingDestinations.Medications = (pendingDestinations.Medications || 0) + additionalMedicationCount;
  }

  const savedItems = Array.isArray(source.careKnowledgeSavedItems)
    ? source.careKnowledgeSavedItems
    : [];
  const matchedSavedReceipts = savedItems.filter((item) => automaticIds.has(item.clientId)).length;
  const savedReceipts = matchedSavedReceipts || (
    savedItems.every((item) => !item.clientId) ? savedItems.length : 0
  );
  const automaticSaved = Math.min(automaticTotal, savedReceipts);
  const automaticSaving = Math.max(0, automaticTotal - automaticSaved);
  const savedAutomaticIds = new Set(
    savedItems.map((item) => item.clientId).filter(Boolean)
  );
  const automaticSavedDestinations = automaticItems.reduce((counts, item, index) => {
    const saved = savedAutomaticIds.size
      ? savedAutomaticIds.has(item.clientId)
      : index < automaticSaved;
    if (!saved) return counts;
    addDestination(counts, item.destination);
    return counts;
  }, {});
  const waitingForReview = Math.max(0, reviewTotal - reviewed);
  const classifiedTotal = automaticTotal + reviewTotal + alreadyKnown + unableToClassify + timelineOnly;
  const declaredUnclassifiedCount = Number(
    source.careKnowledgeSuggestionSummary?.unclassifiedCount
    || source.extractionSummary?.unclassifiedCount
    || 0
  );
  const additionalUnclassified = Math.max(0, declaredUnclassifiedCount - unableToClassify);
  unableToClassify += additionalUnclassified;
  const total = classifiedTotal + additionalUnclassified;

  return {
    total,
    automaticTotal,
    automaticSaved,
    automaticSaving,
    automaticSavedDestinations,
    alreadyKnown,
    reviewTotal,
    reviewed,
    waitingForReview,
    skipped,
    unableToClassify,
    timelineOnly,
    destinations,
    pendingDestinations,
    savedDestinations,
    complete: reviewTotal > 0 && waitingForReview === 0,
  };
};
